import { useState, type FormEvent } from 'react'
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material'
import Add from '@mui/icons-material/Add'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '../api/errors'
import { cboDigits, createJob, jobCodeFromName, JOBS_QUERY_KEY, normalizar } from '../api/jobs'
import { t } from '../i18n'

/**
 * s69 / 2r (Marcelo 29/10/2026 08:44, "como crio um novo cargo"): o cargo nasce pela tela -- so o NOME (e, se ja souber,
 * o CBO). O codigo interno sai do nome (jobCodeFromName). Nome repetido na empresa e barrado AQUI (lista ja carregada) e
 * no servidor (409 pelo codigo). Criado, abre a ficha: dali "Editar descricao" comeca do zero (area, subordinacao,
 * blocos, VIGA) e a funcao e o CBO pela VIGA ja funcionam. Quem ve o botao: quem tem job_architecture.job.write.
 */
export function NewJobButton({ existentes }: { existentes: readonly string[] }) {
  const qc = useQueryClient()
  const ir = useNavigate()
  const [aberto, setAberto] = useState(false)
  const [nome, setNome] = useState('')
  const [cbo, setCbo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const m = useMutation({
    mutationFn: () => createJob(nome, cboDigits(cbo)),
    onSuccess: (id) => {
      void qc.invalidateQueries({ queryKey: JOBS_QUERY_KEY })
      setAberto(false)
      ir(`/cargos/${encodeURIComponent(id)}`)
    },
    onError: (e) => setErro(e instanceof ApiError && e.status === 409 ? t.jobs.newJobDuplicate : t.jobs.newJobError),
  })
  function fechar() {
    setAberto(false)
    setErro(null)
  }
  function enviar(e: FormEvent) {
    e.preventDefault()
    if (!jobCodeFromName(nome)) return setErro(t.jobs.newJobNameInvalid)
    const d = cboDigits(cbo)
    if (d.length !== 0 && d.length !== 6) return setErro(t.jobs.newJobCboInvalid)
    const alvo = normalizar(nome)
    if (existentes.some((n) => normalizar(n) === alvo)) return setErro(t.jobs.newJobDuplicate)
    setErro(null)
    m.mutate()
  }
  return (
    <>
      <Button
        size="small"
        variant="contained"
        startIcon={<Add fontSize="small" />}
        data-testid="novo-cargo"
        onClick={() => {
          setNome('')
          setCbo('')
          setErro(null)
          setAberto(true)
        }}
      >
        {t.jobs.newJob}
      </Button>
      <Dialog open={aberto} onClose={fechar} fullWidth maxWidth="sm">
        <form onSubmit={enviar} data-testid="novo-cargo-form">
          <DialogTitle>{t.jobs.newJobTitle}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                autoFocus
                label={t.jobs.newJobName}
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                slotProps={{ htmlInput: { maxLength: 255 } }}
              />
              <TextField
                label={t.jobs.newJobCbo}
                value={cbo}
                onChange={(e) => setCbo(e.target.value)}
                placeholder="0000-00"
                slotProps={{ htmlInput: { maxLength: 7, inputMode: 'numeric' } }}
                sx={{ maxWidth: 200 }}
              />
              <Typography variant="caption" color="text.secondary">
                {t.jobs.newJobHint}
              </Typography>
              {erro && (
                <Alert severity="warning" data-testid="novo-cargo-erro">
                  {erro}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={fechar}>{t.jobs.newJobCancel}</Button>
            <Button type="submit" variant="contained" disabled={m.isPending}>
              {t.jobs.newJobCreate}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
}
